import { Customer, CustomerAddress, CustomerDocument, CustomerNote, CustomerTag } from '@/api/types/customer';
import { CustomerAddressModel, CustomerDocumentModel, CustomerModel, CustomerNoteModel, CustomerTagModel } from '@/models/customer';
import { transformReservations } from './reservation';
import { transformTag } from './tag';
import { transformUser } from './user';

export function transformCustomerNote(note: CustomerNote) : CustomerNoteModel {
  const createdByUser = note.createdByUser && transformUser(note.createdByUser);
  const customerNoteTags = note.customerNoteTags && transformCustomerTags(note.customerNoteTags);

  return {
    createdByUser,
    customerNoteTags,
    id: note.id,
    content: note.content,
    createdAt: new Date(note.createdAt),
  };
}

export function transformCustomerNotes(notes: CustomerNote[]) : CustomerNoteModel[] {
  return notes.map(transformCustomerNote);
}

export function transformCustomerTag(customerTag: CustomerTag): CustomerTagModel {
  const tag = customerTag.tag && transformTag(customerTag.tag)
  const customerNote = customerTag.customerNote && transformCustomerNote(customerTag.customerNote);

  return {
    tag,
    customerNote,
    id: customerTag.id
  };
}

export function transformCustomerTags(tags: CustomerTag[]): CustomerTagModel[] {
  return tags.map(transformCustomerTag);
}

export function transformCustomerDocument(document: CustomerDocument) : CustomerDocumentModel {
  const expirationDate = document.expirationDate ? new Date(document.expirationDate) : null;
  const issuingDate = document.issuingDate ? new Date(document.issuingDate) : null;
  const issuingCountryCode = document.issuingCountryCode ?? '';

  return {
    expirationDate,
    issuingDate,
    issuingCountryCode,
    id: document.id,
    type: document.type,
    number: document.number
  };
}

export function transformCustomerDocuments(documents: CustomerDocument[]) : CustomerDocumentModel[] {
  return documents.map(transformCustomerDocument);
}

export function transformCustomerAddress(address: CustomerAddress) : CustomerAddressModel {
  const lineOne = address.addressLine1 ?? '';
  const lineTwo = address.addressLine2 ?? '';
  const postalCode = address.postalCode ?? '';

  return {
    lineOne,
    lineTwo,
    postalCode,
    id: address.id,
    city: address.city,
    countryCode: address.countryCode
  };
}

export function transformCustomerAddresses(addresses: CustomerAddress[]) : CustomerAddressModel[] {
  return addresses.map(transformCustomerAddress);
}

export function transformCustomer(customer: Customer) : CustomerModel {
  const customerNotes = customer.customerNotes && transformCustomerNotes(customer.customerNotes)
  const customerTags = customer.customerTags && transformCustomerTags(customer.customerTags)
  const documents = customer.customerDocuments && transformCustomerDocuments(customer.customerDocuments);
  const addresses = customer.customerAddresses && transformCustomerAddresses(customer.customerAddresses);
  const birthDate = customer.birthDate ? new Date(customer.birthDate) : null;
  const nationalityCode = customer.nationalityCode ?? '';
  const phoneNumber = customer.phone ?? '';
  const email = customer.email ?? '';
  const gender = customer.gender ?? '';
  const languageCode = customer.languageCode ?? '';
  const reservations = customer.customerOfReservations && transformReservations(customer.customerOfReservations);

  return {
    customerNotes,
    customerTags,
    documents,
    addresses,
    birthDate,
    nationalityCode,
    email,
    phoneNumber,
    gender,
    languageCode,
    id: customer.id,
    firstName: customer.firstName,
    lastName: customer.lastName,
    totalNights: customer.totalNights,
    totalReservations: customer.totalReservations,
    totalRevenue: customer.totalRevenue,
    primaryAddressId: customer.customerAddressId,
    reservations,
  };
}
